import { AgeGroup } from './modules/main/models/age-group.model';
import { IncomeGroup } from './modules/main/models/income-group.model';
import {
  NameValuePair,
  ToggleableNameValuePair,
} from './modules/main/models/name-value-pair.model';
import { SpendingGroup } from './modules/main/models/spending-group.model';
import { CategoriesResponse } from './modules/main/models/category.model';

const DASHBOARD = '/api/dashboard';
const ADMIN = '/api/admin';
const STATISTICSV2 = '/statistics/v2';
const API = '/api';

export const NEW_LINE = '\n';
export const COMMA = ',';

export const GTM_ID = 'GTM-MW5CJVDB';
interface LegendItemsProps {
  text: string;
  color: string;
  code: string;
  isChecked?: boolean;
}

let uriParts = {
  login: '/login',
  userLogin: ADMIN + '/login',
  resetPasswordRequest: ADMIN + '/users/reset-password-request',
  resetPassword: ADMIN + '/users/reset-password',
  impersonate: ADMIN + '/users/impersonate',
  dashboard: DASHBOARD,
  statistics: DASHBOARD + '/statistics',
  spendingDistribution: DASHBOARD + '/statistics/spending',
  ageGenderComposition: DASHBOARD + '/statistics/age-gender',
  dataVersion: DASHBOARD + '/statistics/data-version',
  exportV2: API + '/exporter/v1/email?type',
  exportDownloadV2: API + '/exporter/v1/download?type',
  customRangeFilters: ADMIN + '/custom-filters/ranges',
  defaultRangeFilters: ADMIN + '/custom-filters/ranges/default',
  customRegionFilters: ADMIN + '/custom-filters/regions',
  accessPlan: ADMIN + '/plans/logged-user',
  customDashboards: ADMIN + '/dashboard',
  user: ADMIN + '/users',
  registerFreemiumUser: ADMIN + '/users/register',
  userEmailDetails: ADMIN + '/users/email-details',
  usersInLoggedOrg: `${ADMIN}/users/logged-organization`,
  usersInOrg: `${ADMIN}/users/organization`,
  loggedUser: ADMIN + '/users/logged-user',
  adminUser: ADMIN + '/users/admin',
  organizations: `${ADMIN}/organizations`,
  plans: `${ADMIN}/plans`,
  contact: `${ADMIN}/contact`,
  onboarding: `/admin/new-user`,
  resetPasswordScreen: `/reset-password`,
  openTool: `/freemium`,
  continentsFeatures: `/assets/json/cont-feat.json`,
  extent: `/assets/json/country_extent.json`,
  features: `/assets/json/feat.json`,
  subnationalFeatures: `/assets/json/subnational_feat.json`,
  continents: `${DASHBOARD}/geography/continents`,
  countries: (continentCode) =>
    `${DASHBOARD}/geography/continents/${continentCode}/countries`,
  cities: (continentCode, countryCode) =>
    `${DASHBOARD}/geography/continents/${continentCode}/countries/${countryCode}/cities`,
  citiesMapList: (continentCode, countryCode) =>
    `${DASHBOARD}/geography/continents/${continentCode}/countries/${countryCode}/cities/coordinates`,
  citiesSearch: `${DASHBOARD}/geography/cities/search`,
  coicopCategories: '/api/coicop-categories/',
  publicCoicopCategories: '/api/coicop-categories/public/',
  notificationsPremiumRequest: `${ADMIN}/notifications/premium-request`,
  notificationsCallMeBack: `${ADMIN}/notifications/call-me-back`,
  statisticsConsumerData: `${DASHBOARD}${STATISTICSV2}/consumer-data`,
  statisticsAgeGenderComposition: `${DASHBOARD}${STATISTICSV2}/age-gender-composition`,
  statisticsTotalDistribution: `${DASHBOARD}${STATISTICSV2}/total-distribution`,
  statisticsContinents: `${DASHBOARD}${STATISTICSV2}/continents`,
  sizeOfPrize: `${DASHBOARD}${STATISTICSV2}/size-of-prize/`,
  sizeOfPrizeTop: `${DASHBOARD}${STATISTICSV2}/size-of-prize/top`,
  sizeOfPrizeGeneralInfo: `${DASHBOARD}${STATISTICSV2}/size-of-prize/general-info`,
  sizeOfPrizeRanking: `${DASHBOARD}${STATISTICSV2}/size-of-prize/ranking`,
  getGenerations: `${DASHBOARD}/statistics/v2/generations`,
};

const generationList = [
  new ToggleableNameValuePair('All generations', 0),
  new ToggleableNameValuePair('Silent (1928-1945)', 1),
  new ToggleableNameValuePair('Baby Boomers (1946-1964)', 2),
  new ToggleableNameValuePair('Gen-X (1965-1980)', 3),
  new ToggleableNameValuePair('Millenials (1981-1996)', 4),
  new ToggleableNameValuePair('Gen-Z (1997-2012)', 5),
  new ToggleableNameValuePair('Alpha (2013-2025)', 6),
  new ToggleableNameValuePair('Unknown (2026-2038)', 7),
];

let data = {
  continents: [
    'Africa',
    'Asia',
    'Europe',
    'North America',
    'Oceania',
    'South America',
    'America do Sul + Oceania',
  ],
  countriesWithSubnationalData: [
    'MEX',
    'AUT',
    'BEL',
    'CZE',
    'DEU',
    'DNK',
    'ESP',
    'FIN',
    'FRA',
    'GBR',
    'GRC',
    'HUN',
    'IRL',
    'ITA',
    'NLD',
    'POL',
    'SVK',
    'SWE',
  ],
  dataLensOptions: [
    new ToggleableNameValuePair('National', 0),
    new ToggleableNameValuePair('Subnational - Provinces', 1, '', true),
    new ToggleableNameValuePair('Subnational - Cities', 2, '', true),
  ],
  genderOptions: [
    new ToggleableNameValuePair('All', undefined),
    new ToggleableNameValuePair('Male', 'male'),
    new ToggleableNameValuePair('Female', 'female'),
  ],
  // todo: use only for open tool
  systemAgeGroups: [
    new AgeGroup(),
    new AgeGroup(0, 15),
    new AgeGroup(15, 30),
    new AgeGroup(30, 45),
    new AgeGroup(45, 65),
    new AgeGroup(65),
  ],
  systemIncomeGroups: [
    new IncomeGroup(),
    new IncomeGroup('Income', 44000),
    new IncomeGroup('Income', 30000, 44000),
    new IncomeGroup('Income', 15000, 30000),
    new IncomeGroup('Income', 5000, 15000),
    new IncomeGroup('Income', 0, 5000),
  ],
  // todo: use only for open tool
  systemSpendingGroups: [
    new SpendingGroup(),
    new SpendingGroup('Rich', 130),
    new SpendingGroup('Consumer class', 13),
    new SpendingGroup('Upper Middle', 90, 130),
    new SpendingGroup('Core Middle', 45, 90),
    new SpendingGroup('Lower Middle', 13, 45),
    new SpendingGroup('Vulnerable and Poor', undefined, 13),
  ],
  spendingDistributionSpendingGroups: [
    new SpendingGroup(undefined, 130, undefined),
    new SpendingGroup(undefined, 90, 130),
    new SpendingGroup(undefined, 45, 90),
    new SpendingGroup(undefined, 13, 45),
    new SpendingGroup(undefined, undefined, 13),
  ],
  spendingPer: [new NameValuePair('Year', 1), new NameValuePair('Day', 0)],
  currencyView: [
    new NameValuePair('Nominal Dollars', 1),
    new NameValuePair('2021 PPP USD', 0),
  ],
  sorting: [
    { text: 'Risers', id: 'risers' },
    { text: 'Decliners', id: 'decliners' },
  ],
  sizeOfPrizesorting: [
    { text: 'Tree map', id: 'treemap' },
    { text: 'Pie chart', id: 'pie' },
    { text: 'Scatter plot', id: 'scatter' },
  ],
  systemDashboards: ['The World'],
};

const legendItems: LegendItemsProps[] = [
  {
    text: 'Asia',
    color: '#8AABFF',
    code: 'AS',
    isChecked: true,
  },
  {
    text: 'Europe',
    color: '#FFC966',
    code: 'EU',
    isChecked: true,
  },
  {
    text: 'North America',
    color: '#9B91D9',
    code: 'NA',
    isChecked: true,
  },
  {
    text: 'South America',
    color: '#F598D0',
    code: 'SA',
    isChecked: true,
  },
  {
    text: 'Africa',
    color: '#63BFB0',
    code: 'AF',
    isChecked: true,
  },
  {
    text: 'Oceania',
    color: '#FA915C',
    code: 'OC',
    isChecked: true,
  },
];

const startYear = 2016;
const categoriesStartYear = 2024;
const freemiumStartYear = 2025;
export const currentYear = new Date().getFullYear();
const endYear = currentYear + 10;

export const constants = {
  uriParts: uriParts,
  currentUserStorageKey: 'currentUser',
  registeredUserOpenToolStorageKey: 'registeredOpenTool',
  startYear: startYear,
  categoriesStartYear: categoriesStartYear,
  freemiumStartYear,
  initialYear:
    currentYear > endYear || currentYear < startYear ? 2024 : currentYear,
  endYear: endYear,
  data: data,
  currentYear,
  generationList,
  legendItems,
};

export const additionalFreemiumCategories: CategoriesResponse = [
  {
    code: '12.1',
    label: 'Beauty',
    childrenSize: 3,
    childrenCategories: [
      {
        code: '',
        label: 'Products',
        isCustom: true,
      },
      {
        code: '',
        label: 'Tools',
        isCustom: true,
      },
      {
        code: '',
        label: 'Services',
        isCustom: true,
      },
    ],
    isCustom: true,
  },
];
